import { useState, useEffect, useRef, useLayoutEffect } from "react";
import AdminNoteService from "src/Services/API/adminnote.service";
import { formatDate, getAvatarUrl, getImageUrl } from "src/Common/common";
import UserService from "src/Services/API/user.service";

const AdminNotes = ({ woId, refreshToken = 0, onHasContent }) => {
  const currentUser = UserService.getCurrentUser();
  const [adminNotes, setAdminNotes] = useState([]);
  const [note, setNote] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const editingTextareaRef = useRef(null);

  useEffect(() => {
    if (woId) {
      getAdminNotes(woId);
    }
  }, [woId, refreshToken]);

  // Notify the parent whether any notes exist (e.g. to highlight the panel)
  useEffect(() => {
    if (onHasContent) {
      onHasContent(adminNotes.length > 0);
    }
  }, [adminNotes]);

  const getAdminNotes = async (woId) => {
    try {
      const response = await AdminNoteService.findAll(woId);
      setAdminNotes(response.data);
    } catch (error) {
      console.log("Error getting admin notes: ", error);
    }
  };

  const sendAdminNote = async () => {
    const trimmedNote = note.trim();
    if (!trimmedNote || !woId || isSending) {
      return;
    }

    try {
      setIsSending(true);
      const response = await AdminNoteService.create({
        woId: woId,
        createdById: currentUser.id,
        note: trimmedNote,
      });
      setAdminNotes((prev) => [...prev, response.data]);
      setNote("");
    } catch (error) {
      console.log("Error creating admin note: ", error);
    } finally {
      setIsSending(false);
    }
  };

  const autoResizeEditingTextarea = () => {
    const textarea = editingTextareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    autoResizeEditingTextarea();
  }, [editingText, editingNoteId]);

  const handleEditClick = (adminNote) => {
    if (editingNoteId === adminNote.id) {
      return;
    }
    setEditingNoteId(adminNote.id);
    setEditingText(adminNote.note || "");
  };

  const saveAdminNote = async (adminNoteId) => {
    const trimmedNote = editingText.trim();
    if (!trimmedNote || isUpdating) {
      return;
    }

    try {
      setIsUpdating(true);
      const response = await AdminNoteService.update(adminNoteId, {
        note: trimmedNote,
        // updatedById: currentUser.id,
      });
      const updatedNote = response.data;
      setAdminNotes((prev) => prev.map((item) => (item.id === adminNoteId ? updatedNote : item)));
      setEditingNoteId(null);
      setEditingText("");
    } catch (error) {
      console.log("Error updating admin note: ", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div>
      {adminNotes.length > 0 && adminNotes?.map((note) => (
        <div key={note.id} className="w-full flex items-start gap-2 mb-3">
          <div className="flex flex-col gap-1 min-w-[180px]">
            <span className="font-family-OpenSansRegular text-[12px] text-titleColor min-w-[140px]">
              {formatDate(note.createdAt)}
            </span>

            {note.updatedAt !== note.createdAt && (
              <span className="font-family-OpenSansRegular text-[11px] text-[#404A6E] min-w-[140px] italic">
                last edited {formatDate(note.updatedAt)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-auto">
            {editingNoteId === note.id ? (
              <textarea
                ref={editingTextareaRef}
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                rows={1}
                className="w-full border-solid border-[1px] border-borderColor rounded-[7px] px-2 py-1 font-family-OpenSansRegular text-[12px] text-titleColor resize-none"
              />
            ) : (
              <span className="font-family-OpenSansRegular text-[12px] text-titleColor flex-auto">{note.note}</span>
            )}
            {note.createdBy.id === currentUser.id && (
              <EditButton
                clicked={() => (editingNoteId === note.id ? saveAdminNote(note.id) : handleEditClick(note))}
                isEditing={editingNoteId === note.id}
                isDisabled={(editingNoteId !== null && editingNoteId !== note.id) || isUpdating}
              />
            )}
          </div>
          <div className="flex items-center gap-2 min-w-[180px]">
            <img src={getAvatarUrl(note.createdBy.avatar)} className="w-[24px] rounded-[12px]" />
            <span className="font-family-OpenSansRegular text-[12px] text-titleColor">
              {note.createdBy.name}
            </span>
          </div>
        </div>
      ))}

      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Write a note"
        rows={3}
        className="w-full border-solid border-[1px] border-borderColor rounded-[7px] p-3 font-family-OpenSansRegular text-[12px] text-titleColor resize-none"
      />
      <div className="w-full flex items-center justify-end mt-2 ">
        <button
          type="button"
          onClick={sendAdminNote}
          disabled={isSending || note.trim() === ""}
          className="uppercase border-solid border-[1px] border-borderColor rounded-[6px] px-5 py-2 font-family-OpenSansRegular text-[14px] leading-none text-titleColor disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ADD NOTE
        </button>
      </div>
    </div>
  );
};

export default AdminNotes;

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  return (
    isEditing ? (
      <div onClick={clicked} className="min-w-[18px] cursor-pointer">
        <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" />
      </div>
    ) : (
      <div
        onClick={isDisabled ? undefined : clicked}
        className={`min-w-[18px] ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
      >
        <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" />
      </div>
    )
  );
};